import { useState, useEffect, useCallback } from 'react';
import type { UserProfile } from '@reqshare/shared';
import { getProfile } from '../api/auth-api';

const JWT_KEY = 'reqshare_jwt';

function getStoredToken(): string | null {
  return localStorage.getItem(JWT_KEY);
}

function clearStoredToken(): void {
  localStorage.removeItem(JWT_KEY);
}

function notifyExtensionLogout(): void {
  window.postMessage({ type: 'REQSHARE_LOGOUT' }, '*');
}

export function useWebAuth() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback((token: string) => {
    getProfile(token)
      .then((profile) => {
        setUser(profile);
      })
      .catch(() => {
        clearStoredToken();
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    // Check for token passed via URL (extension → web)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');

    if (urlToken) {
      localStorage.setItem(JWT_KEY, urlToken);
      // Strip token from URL for security
      params.delete('token');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }

    fetchProfile(token);
  }, [fetchProfile]);

  // Listen for extension auth sync (content script injects token)
  useEffect(() => {
    function handleAuthSync() {
      const token = getStoredToken();
      if (token && !user) {
        setLoading(true);
        fetchProfile(token);
      }
    }

    window.addEventListener('reqshare-auth-sync', handleAuthSync);
    return () => window.removeEventListener('reqshare-auth-sync', handleAuthSync);
  }, [user, fetchProfile]);

  const logout = useCallback(() => {
    clearStoredToken();
    setUser(null);
    notifyExtensionLogout();
  }, []);

  return {
    user,
    token: getStoredToken(),
    isAuthenticated: !!user && !user.isAnonymous,
    isAnonymous: user?.isAnonymous ?? true,
    loading,
    logout,
  };
}
